import type { PulseEventContext, PulseEvent, PulseTrackerConfig } from './types.js';

/**
 * Where a Next.js consumer's route handler lives by convention, and therefore the default.
 *
 * Exported so a consumer who mounted the route elsewhere can say so against a named constant
 * rather than a second copy of the string, and so the README's path and the code's path cannot
 * drift. It lives here rather than in `types.ts` because every cross-module import in this file
 * must stay `import type` — the install-free test suite loads it from source with no node_modules,
 * where a value import of `./types.js` does not resolve.
 */
export const PULSE_DEFAULT_ENDPOINT = '/api/pulse/track';

/**
 * Copies of two constants whose canonical home is `consent.ts`.
 *
 * They are duplicated rather than imported because of the rule stated above: every cross-module
 * import in this file must stay `import type`, and a value import of `./consent.js` does not
 * resolve in the install-free CI step — verified, not assumed. Two string literals is the cheaper
 * debt than moving this module's tests out of that step.
 *
 * `test/emitted/artifact.test.ts` asserts these equal `consent.ts`'s, so the two cannot drift.
 */
const CONSENT_COOKIE_NAME = '__analysis_cookies_opt_in';
const CONSENT_WITHDRAWN_EVENT_TYPE = 'consent_withdrawn';

/**
 * A copy of `contextViolation` in `payload.ts`, for the same reason as the constants above:
 * `payload.ts` is the canonical rule, and `test/core.test.ts` runs both over one set of cases so
 * they cannot drift.
 *
 * Checked before sending because an event the ingest cannot store is refused anyway (CIN-15332) —
 * and refusing it here names the offending key in the console of the developer who wrote it,
 * rather than in a 400 from a route handler they may not be watching.
 */
const CONTEXT_STRING_MAX_LENGTH = 50;
const MAX_SHOWING_ID = 2_147_483_647;

function contextViolation(context: PulseEventContext): { key: string; error: string } | null {
    for (const key of ['page', 'trailer_id']) {
        const value = context[key];
        if (value === undefined || value === null) continue;

        if (typeof value !== 'string' && !(typeof value === 'number' && Number.isSafeInteger(value))) {
            return { key, error: `context.${key} must be a string` };
        }
        if ([...String(value)].length > CONTEXT_STRING_MAX_LENGTH) {
            return {
                key,
                error: `context.${key} exceeds ${CONTEXT_STRING_MAX_LENGTH} characters (a normalized route, not a raw URL)`
            };
        }
    }

    const showingId = context.showing_id;
    if (showingId !== undefined && showingId !== null) {
        const asNumber = typeof showingId === 'string' && /^\d+$/.test(showingId) ? Number(showingId) : showingId;
        if (
            !(typeof asNumber === 'number' && Number.isInteger(asNumber) && asNumber > 0 && asNumber <= MAX_SHOWING_ID)
        ) {
            return { key: 'showing_id', error: 'context.showing_id must be a positive integer' };
        }
    }

    return null;
}

/**
 * Merges an event's context over the tracker's default context.
 *
 * Every key replaces, as a shallow spread would — except `external`, which is merged one level
 * deeper. A surface sets its cinema once in `defaultContext` and each event adds the axis it knows
 * (`showing` at checkout, `entity` on a film page); a plain spread let the event's `external`
 * replace the whole object and silently dropped the cinema. That is the one id the backend needs to
 * resolve anything at all, and the loss is invisible — the event still arrives, just unattributable.
 *
 * The merge stops at the axis. Two callers both naming `external.cinema` is not a case correct usage
 * produces: `externalRef()` takes every axis in one call, so a per-event cinema id is meant to
 * replace the default outright.
 */
function mergeContext(
    base: PulseEventContext | undefined,
    override: PulseEventContext | undefined
): PulseEventContext | undefined {
    if (!base && !override) {
        return undefined;
    }

    const merged: PulseEventContext = { ...base, ...override };

    if (base?.external || override?.external) {
        merged.external = { ...base?.external, ...override?.external };
    }

    return merged;
}

/**
 * Creates a tracker bound to the given configuration.
 *
 * Sends events to a same-origin endpoint the consumer owns, which enriches the payload server-side
 * with anonymous_id, tenant_id and source_type and attaches the bearer token. `endpoint` defaults
 * to {@link PULSE_DEFAULT_ENDPOINT}, so a consumer who mounted the route where the README says to
 * passes no configuration at all.
 */
export function createTracker(config: PulseTrackerConfig = {}) {
    // Every option is read per event, never captured here: `PulseProvider` keeps one tracker for
    // its lifetime and hands it a live view of the latest props, so a captured `consent` would be
    // the first render's closure for good.
    function hasConsent(): boolean {
        return config.consent ? config.consent() : defaultConsentReader(config.consentCookieName);
    }

    // A failing endpoint logs once, not once per event. On a partner's production site the old
    // behavior was one console error per navigation for a condition no visitor can act on, and
    // that noise is what gets an analytics SDK ripped out. The first line still lands — including
    // in a production build — because a silent SDK makes "misconfigured" and "nobody has visited
    // yet" look identical.
    let hasLoggedFailure = false;

    function logFailureOnce(...args: unknown[]): void {
        if (hasLoggedFailure) return;

        hasLoggedFailure = true;
        console.error(...args, '— further pulse failures are not logged.');
    }

    // Once per offending key: a surface that sends a raw pathname does it on every navigation, and
    // the first warning already says which key and why.
    const warnedKeys = new Set<string>();

    return {
        /**
         * The same read the send gate below asks. Exposed for a caller that has to decide something
         * *before* sending — `PulsePageView` holds a view rather than letting `track()` drop it — so
         * the two can never disagree about a partner's CMP.
         */
        hasConsent(): boolean {
            return hasConsent();
        },

        async track(event: PulseEvent): Promise<void> {
            // ADR 0018: nothing before consent. Checked here to save a round trip a visitor who
            // said no gains nothing from — the route handler asks the same question server-side and
            // that answer is the one that decides. A tracker that silently kept posting would still
            // be correct; one that silently stopped posting when consent *was* given would not, so
            // the cheap check lives on the side that fails safe.
            //
            // The withdrawal is the exception and has to be: by the time it fires the consent
            // cookie is already gone, so gating it would swallow the one message that records the
            // withdrawal, and the backend would never learn of it. ADR 0018 again — silence is
            // indistinguishable from inactivity, so simply stopping is not a withdrawal.
            if (event.event_type !== CONSENT_WITHDRAWN_EVENT_TYPE && !hasConsent()) {
                return;
            }

            const context = mergeContext(config.defaultContext, event.context);

            // Dropped, not truncated: two long routes sharing a 50-character prefix would silently
            // collapse into one value, and an aggregate that is quietly wrong is worse than one
            // that is visibly missing an event.
            const violation = context ? contextViolation(context) : null;
            if (violation) {
                if (!warnedKeys.has(violation.key)) {
                    warnedKeys.add(violation.key);
                    console.error(
                        `Pulse event "${event.event_type}" not sent: ${violation.error}.`,
                        '— further events with this problem are dropped silently.'
                    );
                }
                return;
            }

            try {
                const response = await fetch(config.endpoint ?? PULSE_DEFAULT_ENDPOINT, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    // Callers do not await track() before letting a navigation proceed — an ad
                    // click is an anchor with an onClick, so the request is in flight exactly as
                    // the document unloads, and a plain fetch() is cancelled at that point. That
                    // silently drops `ad_click`, which is the numerator of every cross-campaign
                    // ratio (CIN-14635), while impressions and closes — which never navigate —
                    // keep arriving, so the loss reads as "nobody clicks" rather than as a bug.
                    // Payloads are a few hundred bytes, far under keepalive's 64KB body limit.
                    keepalive: true,
                    // No user_id: the route handler derives identity from its own session resolver
                    // and discards anything the browser sends, so putting one here would look like
                    // it worked and change nothing.
                    body: JSON.stringify({ ...event, context })
                });

                if (!response.ok) {
                    const text = await response.text().catch(() => '');
                    logFailureOnce(`Pulse track failed (${response.status}): ${text}`);
                }
            } catch (error) {
                logFailureOnce('Pulse track error:', error);
            }
        }
    };
}

export type PulseTracker = ReturnType<typeof createTracker>;

/**
 * The default client-side consent read: Cineamo's own banner cookie, or the one named in
 * `consentCookieName`.
 *
 * Returns `false` wherever `document` does not exist — a server render, a worker, a test — because
 * the alternative is a tracker that posts during SSR against a visitor whose decision it cannot
 * see. Only a literal `true` counts; absence is not consent.
 */
function defaultConsentReader(cookieName: string = CONSENT_COOKIE_NAME): boolean {
    if (typeof document === 'undefined') return false;

    return document.cookie.split(';').some((part) => {
        const separator = part.indexOf('=');

        return (
            separator !== -1 &&
            part.slice(0, separator).trim() === cookieName &&
            part.slice(separator + 1).trim() === 'true'
        );
    });
}
